import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Mandatory ToS acknowledgement gate. Renders a full-screen modal until the
 * user explicitly checks the disclaimer box and clicks Continue.
 * Acknowledgement is stored on user's record server-side AND in localStorage.
 */
const REQUIRED_TEXT = "I reviewed the terms of service and understand that the information inside INSTITUTIONAL SANDBOX is NOT financial advice";

export default function TosGate() {
  const { user, refreshMe } = useAuth();
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  // Determine if user needs to accept ToS
  const accepted = user?.tos_accepted_at || (() => {
    try { return localStorage.getItem(`tos.accepted.${user?.id || "anon"}`) === "1"; } catch { return false; }
  })();

  if (!user || accepted) return null;

  async function accept() {
    if (!checked) { setErr("You must check the acknowledgement box first."); return; }
    setSaving(true);
    try {
      await api.post("/auth/accept-tos", {});
      try { localStorage.setItem(`tos.accepted.${user.id}`, "1"); } catch {}
      await refreshMe();
    } catch (e) {
      // Network/endpoint may not exist in older builds — still gate via localStorage so UX isn't blocking
      try { localStorage.setItem(`tos.accepted.${user.id}`, "1"); } catch {}
      window.location.reload();
    } finally { setSaving(false); }
  }

  return (
    <div data-testid="tos-gate" className="fixed inset-0 bg-black/95 backdrop-blur-xl z-[100] flex items-center justify-center p-4 font-mono">
      <div className="bg-[#0f131b] border-2 border-amber-500/40 rounded-2xl max-w-2xl w-full p-6 shadow-2xl">
        <div className="flex items-center gap-3 mb-4 pb-3 border-b border-slate-800">
          <i className="fa-solid fa-file-contract text-3xl text-amber-300"/>
          <div>
            <h2 className="text-xl font-bold text-white">Welcome to INSTITUTIONAL SANDBOX</h2>
            <p className="text-[11px] text-slate-500">Before entering, please acknowledge the disclaimer below.</p>
          </div>
        </div>

        <div className="text-[11px] text-slate-300 space-y-2 mb-4 max-h-72 overflow-y-auto pr-2">
          <p className="text-amber-200 font-bold uppercase tracking-wide">Educational Use Only — Not Financial Advice</p>
          <p>INSTITUTIONAL SANDBOX is a <span className="text-amber-300 font-bold">simulated trading environment</span> built for education and practice. Nothing on this platform constitutes financial advice, an offer to buy or sell securities, or a recommendation of any investment strategy. All market data, AI commentary, and prompts are for instructional purposes only.</p>
          <p>Simulated trading results do <span className="font-bold">not</span> guarantee real-world performance. Real trading carries substantial risk including the possible loss of all principal. You should consult a licensed financial advisor before making any real-money trading decisions.</p>
          <p>By continuing, you confirm that you have read the Terms of Service in your Account → Terms tab and you will not hold INSTITUTIONAL SANDBOX, its operators, or contributors liable for any real-money trading losses you may incur.</p>
        </div>

        {err && <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded px-2 py-1 mb-3">{err}</div>}

        <label className="flex items-start gap-3 bg-slate-900/60 border border-slate-800 rounded-lg p-3 cursor-pointer mb-4">
          <input data-testid="tos-checkbox" type="checkbox" checked={checked} onChange={() => setChecked(c => !c)}
            className="mt-0.5 w-5 h-5 accent-amber-400 cursor-pointer flex-shrink-0"/>
          <span className="text-xs text-slate-200">{REQUIRED_TEXT}</span>
        </label>

        <button data-testid="tos-continue" onClick={accept} disabled={!checked || saving}
          className={`w-full py-3 rounded-lg text-sm font-black uppercase tracking-wider transition-all ${
            checked && !saving
              ? "bg-gradient-to-r from-amber-500 to-rose-500 text-slate-900 hover:from-amber-400 hover:to-rose-400 shadow-lg shadow-amber-500/30"
              : "bg-slate-800 text-slate-600 cursor-not-allowed"}`}>
          {saving ? "Saving…" : "Continue → Enter Sandbox"}
        </button>
      </div>
    </div>
  );
}
